/**
 * Equipment — hand-over of a booking to a colleague (migration 28): the
 * banner with accept / approve / withdraw. (The holder proposes one from
 * My requests → "Hand over to a colleague", EquipmentMine.tsx.)
 *
 * A transfer is proposed by the current holder (or an admin) and becomes
 * effective only when BOTH the new holder and an admin have accepted.
 * Both are emailed at once and may answer in any order; either side can
 * decline, and the proposer can withdraw. All writes go through the
 * SECURITY DEFINER RPCs, which carry the role checks.
 */
import type { JSX } from "preact";
import { useState } from "preact/hooks";
import { isDemoMode } from "../state/store";
import { displayName } from "../utils/names";
import { restRpc } from "../utils/withRetry";
import { toast } from "../utils/toast";
import { transferWaitingOn, type EquipmentTransfer } from "../utils/equipment";
import { fmtStamp } from "./equipmentShared";

/* ─────────────────── open-transfer banner + actions ──────────────── */

export function TransferBanner({
  transfer: t, me, profileId, isAdmin, onChanged, compact,
}: {
  transfer: EquipmentTransfer;
  me: string;
  profileId: string | null;
  isAdmin: boolean;
  onChanged: () => Promise<void>;
  /** Buttons only (inside a "Waiting for you" item). */
  compact?: boolean;
}): JSX.Element | null {
  const [busy, setBusy] = useState(false);
  const waiting = transferWaitingOn(t);
  if (!waiting) return null;
  const demo = isDemoMode.value;
  // In demo mode there is no auth id — match on the roster name instead.
  const isTarget = demo ? t.to_name === me : t.to_profile_id === profileId;
  const isProposer = demo ? t.from_name === me : (!!profileId && t.from_profile_id === profileId);

  const run = async (label: string, fn: () => Promise<unknown>, done: string): Promise<void> => {
    if (demo) { toast(`Demo mode — ${label} isn't saved.`); return; }
    setBusy(true);
    try {
      await fn();
      toast(done);
      await onChanged();
    } catch (err) {
      toast(err instanceof Error ? err.message : `${label} failed`);
    } finally {
      setBusy(false);
    }
  };
  const accept = (): Promise<void> => run(
    "accepting",
    async () => {
      const r = await restRpc<boolean>("equipment_accept_transfer", { p_transfer_id: t.id });
      return r[0];
    },
    t.approved_at ? "Hand-over accepted — the booking is now yours" : "Hand-over accepted — waiting for an admin to approve it",
  );
  const declineAsTarget = (): Promise<void> => {
    const reason = prompt("Decline the hand-over — optional note for the colleague who proposed it:") ?? "";
    return run("declining", () => restRpc("equipment_decline_transfer", { p_transfer_id: t.id, p_reason: reason }), "Hand-over declined");
  };
  const approve = (): Promise<void> => run(
    "approving",
    () => restRpc<boolean>("equipment_decide_transfer", { p_transfer_id: t.id, p_approve: true, p_reason: "" }),
    t.accepted_at ? `Transfer approved — ${displayName(t.to_name)} now holds the booking` : `Transfer approved — waiting for ${displayName(t.to_name)} to accept`,
  );
  const reject = (): Promise<void> => {
    const reason = prompt("Reject the hand-over — reason shown to the holder:") ?? "";
    if (!reason.trim()) { toast("A reason is required to reject."); return Promise.resolve(); }
    return run("rejecting", () => restRpc("equipment_decide_transfer", { p_transfer_id: t.id, p_approve: false, p_reason: reason }), "Transfer rejected");
  };
  const withdraw = (): Promise<void> => {
    if (!confirm(`Withdraw the hand-over to ${displayName(t.to_name)}?`)) return Promise.resolve();
    return run("withdrawing", () => restRpc("equipment_decline_transfer", { p_transfer_id: t.id, p_reason: "" }), "Hand-over withdrawn");
  };

  const buttons = (
    <div class="eq-booking-actions">
      {isTarget && !t.accepted_at && (
        <>
          <button type="button" class="eq-action eq-action-primary" disabled={busy} onClick={() => void accept()}>Accept the hand-over</button>
          <button type="button" class="eq-action eq-action-quiet" disabled={busy} onClick={() => void declineAsTarget()}>Decline…</button>
        </>
      )}
      {isAdmin && !t.approved_at && (
        <>
          <button type="button" class="eq-action" disabled={busy} onClick={() => void approve()}>Approve transfer</button>
          <button type="button" class="eq-action eq-action-danger" disabled={busy} onClick={() => void reject()}>Reject…</button>
        </>
      )}
      {(isProposer || isAdmin) && !compact && (
        <button type="button" class="eq-action eq-action-quiet" disabled={busy} onClick={() => void withdraw()}>Withdraw</button>
      )}
    </div>
  );
  if (compact) return buttons;

  return (
    <div class="eq-transfer" role="status">
      <div class="eq-transfer-head">
        <b>↪ Hand-over to {displayName(t.to_name)}</b>
        <span class="eq-transfer-meta">
          proposed by {displayName(t.proposed_by || t.from_name)} · {fmtStamp(t.proposed_at)}
        </span>
      </div>
      <div class="eq-transfer-state">
        <span class={t.accepted_at ? "ok" : ""}>{t.accepted_at ? "✓" : "○"} {displayName(t.to_name)} {t.accepted_at ? "accepted" : "to accept"}</span>
        <span class={t.approved_at ? "ok" : ""}>{t.approved_at ? "✓" : "○"} admin {t.approved_at ? `approved (${displayName(t.approved_by)})` : "to approve"}</span>
        <span class="eq-transfer-wait">Effective once both have said yes · awaiting {waiting}</span>
      </div>
      {buttons}
    </div>
  );
}
